import { Modal, Image, Stack, Group, Text, ActionIcon, SimpleGrid, Card, Badge, Tooltip, Center, Loader } from '@mantine/core';
import { IconScale, IconScaleOff, IconHeart, IconCrosshair, IconFlag, IconX, IconArrowLeft } from '@tabler/icons-react';
import { RarityBadge } from '@/components/RarityBadge';
import { ItemCard } from '@/components/ItemCard';
import { Popularity } from '@/components/Popularity';
import { formatKNumber, translate } from '@/lib';
import { useStore } from '@/store';
import { useItemDetail, type ApiItem, type ApiItemDetail } from '@/models/Item';
import { getCategoryName, getGenreName, getRareInfo } from '@/models/Metadata';
import { useQuery } from '@tanstack/react-query';
import { getItem } from '@/client/sdk.gen';
import style from './Style.module.scss';
import { AppearingInfo } from './AppearingInfo';
import { ReportForm } from './ReportForm';
import { useState } from 'react';

interface ItemDetailModalProps {
  item: ApiItem | ApiItemDetail | null;
  opened: boolean;
  onClose: () => void;
  onItemSelect?: (item: ApiItem | ApiItemDetail) => void;
}

export function ItemDetailModal({ item: initialItem, opened, onClose, onItemSelect }: ItemDetailModalProps) {
  const { compareList, isIngameBrowser, addToCompare, removeFromCompare, isInCompareList } = useStore();
  const [isShowingReport, setIsShowingReport] = useState(false);

  // Fetch full item detail from API when modal opens
  const { data: apiItem, isLoading: isLoadingItem } = useItemDetail(
    opened && initialItem ? initialItem.id : undefined
  );

  // Use API data if available, fallback to initial item
  const item = apiItem || initialItem;

  // Fetch subItems if they exist
  const subItemIds = item?.subItems || [];
  const { data: subItemsData, isLoading: isLoadingSubItems } = useQuery({
    queryKey: ['subItems', subItemIds],
    queryFn: async () => {
      const results = await Promise.all(
        subItemIds.map(async (id) => {
          try {
            const { data } = await getItem({ 
              path: { id: Number(id) }, 
              throwOnError: true 
            });
            return data;
          } catch {
            return null;
          }
        })
      );
      return results.filter((item): item is ApiItemDetail => item !== null);
    },
    enabled: opened && subItemIds.length > 0,
  });

  if (!item) return null;

  // Get metadata info
  const rareInfo = getRareInfo(item.rare);
  const categoryName = getCategoryName(item.category);
  const genreName = getGenreName(item.genre);
  
  // Get tier background class name
  const tierBgClass = item.rare ? `tier-bg-${item.rare}` : 'tier-bg-1';

  // Category checks
  // const isOutfit = item.category === 1;

  const inCompareList = isInCompareList(item?.id);
  const compareListFull = compareList.length >= 3;

  const handleModalClose = () => {
    setIsShowingReport(false);
    onClose();
  };

  const handleCompareClick = () => {
    if (inCompareList) {
      removeFromCompare(item?.id);
    } else if (!compareListFull) {
      addToCompare(item as any); // Store expects Item type, will need update
    }
  };

  const subItems = subItemsData || [];

  const statEntries = item?.stats ? [
    item.stats.damage && { value: item.stats.damage, label: translate('ff.item.stats.damage', 'Damage') },
    item.stats.accuracy && { value: item.stats.accuracy, label: translate('ff.item.stats.accuracy', 'Accuracy') },
    item.stats.range && { value: item.stats.range, label: translate('ff.item.stats.range', 'Range') },
    item.stats.speed && { value: item.stats.speed, label: translate('ff.item.stats.speed', 'Rate of Fire') },
    item.stats.reload && { value: item.stats.reload, label: translate('ff.item.stats.reload', 'Reload Speed') },
    item.stats.cartridge && { value: item.stats.cartridge, label: translate('ff.item.stats.cartridge', 'Magazine') },
    item.stats.biteArmor && { value: item.stats.biteArmor, label: translate('ff.item.stats.biteArmor', 'Armor Penetration') },
    item.stats.playerSpeedFactor && { value: item.stats.playerSpeedFactor, label: translate('ff.item.stats.playerSpeedFactor', 'Movement Speed') },
  ].filter(Boolean) as { value: number; label: string }[] : [];

  return (
    <Modal
      opened={opened}
      onClose={handleModalClose}
      size="lg"
      classNames={{ body: style.modalBody, title: style.modalTitle }}
      withCloseButton={false}
      title={
        <Group gap="sm" justify="space-between" wrap="nowrap" w="100%">
          <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            {isShowingReport && (
              <ActionIcon variant="subtle" color="gray" onClick={() => setIsShowingReport(false)} size="sm">
                <IconArrowLeft size={16} />
              </ActionIcon>
            )}
            <Text fw={700} size="lg" truncate>
              {isShowingReport ? translate('ff.report.title', 'Report Item') : (item.translate || item.name)}
            </Text>
          </Group>
          <Group gap={4} wrap="nowrap">
            {!isShowingReport && (
              <Tooltip label={translate('ff.report.title', 'Report Item')}>
                <ActionIcon variant="subtle" color="orange" onClick={() => setIsShowingReport(true)} size="sm">
                  <IconFlag size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            <ActionIcon variant="subtle" color="gray" onClick={handleModalClose} size="sm">
              <IconX size={16} />
            </ActionIcon>
          </Group>
        </Group>
      }
    >
      <Stack gap="lg">
        {/* Loading indicator */}
        {isLoadingItem ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : (
          <>
            {/* Header with image and title */}
            <Group align="flex-start" wrap="nowrap">
              <div className={`${style.imageContainer} ${tierBgClass}`}>
                <Image
                  src={item.icon}
                  alt={item.name}
                  width={120}
                  height={120}
                  fit="cover"
                  radius="md"
                />
                {item?.stats?.evoLevel && (
                  <Badge className={style.evoBadge} size="xs" variant="filled" color="green" radius="sm">
                    {translate('ff.item.stats.evoLevel', 'Evo')}: {item.stats?.evoLevel}
                  </Badge>
                )}
              </div>

              <Stack style={{ flex: 1 }} gap="xs">
                <div>
                  {/* <Text fw={700} size="xl">{item.name}</Text> */}
                  <Group gap="xs" mt="xs">
                    <RarityBadge rarity={rareInfo.name as any} rare={item.rare} />
                    {genreName && (
                      <Badge size="sm" variant="dot" color="blue">
                        {genreName}
                      </Badge>
                    )}
                  </Group>
                </div>

                <Group gap="xs">
                  {item?.stats && (
                    <Tooltip 
                      label={inCompareList ? translate('ff.action.removeFromCompare', 'Remove from compare') : compareListFull ? translate('ff.action.compareListFull', 'Compare list full (max 3)') : translate('ff.action.addToCompare', 'Add to compare')}
                    >
                      <ActionIcon
                        variant={inCompareList ? 'filled' : 'light'}
                        color={inCompareList ? 'orange' : 'gray'}
                        onClick={handleCompareClick}
                        size="lg"
                        disabled={!inCompareList && compareListFull}
                      >
                        {inCompareList ? <IconScaleOff size={20} /> : <IconScale size={20} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {/* Wishlist count display */}
                  <Group gap={4} wrap="nowrap">
                    <IconHeart size={20} color="var(--mantine-color-red-5)" />
                    <Text size="sm" c="dimmed" fw={500}>
                      {formatKNumber(item.wishlist)}
                    </Text>
                  </Group>
                </Group>
                {item.isOwned && (
                  <Badge size="xs" variant="filled" color="teal" radius="sm">
                    {translate('ff.item.owned', 'Owned')}
                  </Badge>
                )}
                {/* if isGun, show the gunType in item.stats */}
                {item?.stats?.gunType && (
                  <Badge size="sm" variant="gradient" gradient={{ from: 'rgba(255, 213, 128, 1)', to: 'yellow', deg: 146 }} leftSection={<IconCrosshair size={16} />} color="orange">
                    {item.stats.gunType}
                  </Badge>
                )}
              </Stack>
            </Group>



            {isShowingReport ? (
              <ReportForm itemId={item.id} onSuccess={() => setIsShowingReport(false)} />
            ) : (
              <>
                {/* Popularity Section */}
                <Popularity 
                  itemId={item?.id}
                  likes={item.like}
                  dislikes={item.dislike}
                  reaction={item.reaction}
                  rank={item.rank}
                  categoryRank={item.categoryRank}
                  genreRank={item.genreRank}
                  categoryId={item.category}
                  genreId={item.genre}
                  categoryName={categoryName?.toUpperCase() || 'ITEM'} 
                  genreName={genreName?.toUpperCase() || 'ITEM'}
                  variant="full"
                />

                {/* Appearing Info Section */}
                <AppearingInfo
                  appearInId={item.firstAppearedIn}
                  appearInDate={item.firstAppearedAt}
                  appearLastId={item.lastAppearedIn}
                  appearLastDate={item.lastAppearedAt}
                />

                {/* Outfit-specific sections */}
                {subItemIds?.length > 0 && (
                  <div>
                    <Text fw={600} size="md" mb="md">
                      {translate('ff.item.setContents', 'Set Contents')}
                    </Text>
                    {isLoadingSubItems ? (
                      <Center py="md" w="100%">
                        <Loader size="sm" />
                      </Center>
                    ) : (
                      <Stack gap={isIngameBrowser ? 0 : 'sm'} className={isIngameBrowser ? 'itemContainerRow' : ''}>
                        {subItems.map((subItem) => (
                          <ItemCard
                            key={subItem.id}
                            item={subItem}
                            onClick={onItemSelect}
                            isDetail={true}
                          />
                        ))}
                      </Stack>
                    )}
                  </div>
                )}

                {/* Gun-specific sections - Weapon Stats */}
                {statEntries.length > 0 && (
                  <Card padding="md" radius="md" withBorder>
                    <Text fw={600} size="md" mb="md">
                      {translate('ff.item.weaponStats', 'Weapon Stats')}
                    </Text>
                    <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
                      {[...statEntries].sort((a, b) => b.value - a.value).map((stat, index) => (
                        <div key={index} className={style.statItem}>
                          <Text size="xl" fw={700} c={stat.value > 0 ? 'green' : stat.value < 0 ? 'red' : undefined}>
                            {stat.value > 0 && '+'}{stat.value}
                          </Text>
                          <Text size="xs" c="dimmed" tt="capitalize">{stat.label}</Text>
                        </div>
                      ))}
                    </SimpleGrid>
                  </Card>
                )}
              </>
            )}
          </>
        )}
      </Stack>
    </Modal>
  );
}
